import { mdiAccount, mdiCalendarSync, mdiClockEditOutline, mdiShieldKey } from "@mdi/js"
import {
  buildViewNavigation,
  resolvePageEntry,
  type PageTabWithId,
  type ViewPageSnippets,
  type ViewPageSpec,
} from "@kontextwerk/web-sdk/core/navigation/viewNavigation"
import type { ProfileMenuPage } from "@kontextwerk/web-sdk/core/navigation/profileMenu"
import { getTabs as getGeneralTabs } from "./pages/general/navigation"
import { getTabs as getPermissionsTabs } from "./pages/permissions/navigation"

// Credentials, MFA and devices stay with the personal /account pages (KW-SEC-AUT-014); the member
// record shows the profile, the membership role and the KB product pages.
type UserViewFrameContext = {
  user?: { id?: string | null } | null
  currentUserId?: string | null
  canViewCalendarIntegration?: boolean
  canDisconnectOwnCalendarIntegration?: boolean
}

export type UserViewPaths = {
  general: string
  permissions: string
  integrations: string
  workingHours: string
}

export const buildUserViewPaths = (
  pathFor?: ((pageId: string) => string) | null,
): UserViewPaths => ({
  general: pathFor ? pathFor("general") : "",
  permissions: pathFor ? pathFor("permissions") : "",
  integrations: pathFor ? pathFor("integrations") : "",
  workingHours: pathFor ? pathFor("working-hours") : "",
})

const getIntegrationsTabs = (ctx: UserViewFrameContext): PageTabWithId[] =>
  ctx.canViewCalendarIntegration || ctx.canDisconnectOwnCalendarIntegration
    ? [{ id: "integrations", title: "Integrationen", widthType: "small" }]
    : []

const getWorkingHoursTabs = (_ctx: UserViewFrameContext): PageTabWithId[] => [
  { id: "working-hours", title: "Arbeitszeiten", widthType: "small" },
]

const PAGE_SPECS: Array<ViewPageSpec<UserViewFrameContext, UserViewPaths>> = [
  {
    id: "general",
    title: "Allgemein",
    icon: mdiAccount,
    widthType: "small",
    resolvePath: (paths) => paths.general,
    getTabs: getGeneralTabs,
  },
  {
    id: "permissions",
    title: "Rolle & Berechtigungen",
    icon: mdiShieldKey,
    widthType: "small",
    resolvePath: (paths) => paths.permissions,
    getTabs: getPermissionsTabs,
  },
  {
    id: "integrations",
    title: "Integrationen",
    icon: mdiCalendarSync,
    widthType: "small",
    resolvePath: (paths) => paths.integrations,
    canOpen: (_capabilities, ctx) =>
      Boolean(ctx.canViewCalendarIntegration || ctx.canDisconnectOwnCalendarIntegration),
    getTabs: getIntegrationsTabs,
  },
  {
    id: "workingHours",
    title: "Arbeitszeiten",
    icon: mdiClockEditOutline,
    widthType: "small",
    resolvePath: (paths) => paths.workingHours,
    getTabs: getWorkingHoursTabs,
  },
]

// The frame's profile menu already links the personal account pages; it gains only KB product pages.
const PROFILE_MENU_PAGE_IDS: ReadonlySet<string> = new Set(["integrations", "workingHours"])

export const buildUserViewNavigation = ({
  context,
  paths,
  snippets,
}: {
  context: UserViewFrameContext
  paths: UserViewPaths
  snippets: ViewPageSnippets
}) =>
  buildViewNavigation({
    specs: PAGE_SPECS,
    context,
    paths,
    capabilities: null,
    snippets,
  })

export const buildUserProfilePages = (
  userId: string,
  pathFor: (pageId: string) => string,
): ProfileMenuPage[] =>
  PAGE_SPECS.filter((spec) => PROFILE_MENU_PAGE_IDS.has(spec.id)).flatMap((spec) => {
    const page = resolvePageEntry({
      spec,
      context: {
        user: { id: userId },
        currentUserId: userId,
        canDisconnectOwnCalendarIntegration: true,
      },
      paths: buildUserViewPaths(pathFor),
      capabilities: null,
    })

    return page ? [{ id: page.id!, title: page.title, href: page.path, icon: page.icon }] : []
  })
